import { type RefObject } from 'react';
import {
  EventDetails,
  OpenFeature,
  OpenFeatureError,
  ProviderEvents,
  type Hook,
} from '@openfeature/react-sdk';
import { FeatureFlagError, FeatureFlagErrorCode, getFeatureFlagErrorDetails } from '../errors';
import type { OnErrorCallback } from '../types';

export function subscribeProviderEvents(onErrorRef: RefObject<OnErrorCallback | undefined>) {
  OpenFeature.addHandler(ProviderEvents.Error, (details?: EventDetails) => {
    onErrorRef.current?.(
      new FeatureFlagError(FeatureFlagErrorCode.PROVIDER_ERROR, details?.message ?? 'Unknown')
    );
  });
}

export function createErrorHook(onErrorRef: RefObject<OnErrorCallback | undefined>): Hook {
  return {
    error(hookContext, error) {
      const metadata = {
        flagKey: hookContext.flagKey,
        flagValueType: hookContext.flagValueType,
      };

      if (error instanceof OpenFeatureError) {
        const details = getFeatureFlagErrorDetails(error.code);
        const message = error.message.length ? error.message : details.message;
        onErrorRef.current?.(new FeatureFlagError(details.code, message, metadata));
        return;
      }

      onErrorRef.current?.(
        new FeatureFlagError(
          FeatureFlagErrorCode.GENERAL_ERROR,
          error instanceof Error ? error.message : 'Unknown error',
          metadata
        )
      );
    },
  };
}

export function registerErrorHook(onErrorRef: RefObject<OnErrorCallback | undefined>): void {
  OpenFeature.addHooks(createErrorHook(onErrorRef));
}

export function createProviderLogger(onErrorRef: RefObject<OnErrorCallback | undefined>) {
  return {
    debug: (_message: string) => {},
    info: (_message: string) => {},
    warn: (_message: string) => {},
    error: (message: string) => {
      onErrorRef.current?.(new FeatureFlagError(FeatureFlagErrorCode.PROVIDER_ERROR, message));
    },
  };
}
